import { ensureDefined } from "common/utils/assertOfType";
import { polyLine2EdgeEvalOffset, polyLine2EdgeNormal } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import type { Ray2 } from "geom/ray2Types";
import { getIntervalWidth } from "./wallInterval";
import type { TraverseOptions, WallInterval, WallIntervalLoop } from "./wallTraverseTypes";
import { WallIntervalEntityType } from "./wallTraverseTypes";

export const DefaultDoorMarchTraversalOptions: TraverseOptions = {
  ignoreEntityInterval: true,
  avoidDoors: true,
  avoidDoorSwings: true,
  avoidWindows: true,
  avoidWallCuts: true,
  stopAtNextDoor: true,
  perimeterTravelLimit: 60,
  minimumIntervalWidth: 4,
  placementIntervalWidth: 2,
};

/**
 *  Enum to represent the possible actions to take when traversing a wall interval
 * */
enum IntervalAction {
  EXIT,
  SKIP,
  PLACE,
}

/**
 * Function to traverse a wall interval loop in the counter-clockwise direction starting
 * from a given edge and interval index.
 * @param loop
 * @param edgeIndex The index of the edge to start traversing from
 * @param intervalIndex The index of the interval to start traversing from
 * @param options configuration options for the traversal
 * @see traverseWallIntervalLoopCW
 */
export function traverseWallIntervalLoopCCW(
  loop: WallIntervalLoop,
  edgeIndex: number,
  intervalIndex: number,
  options: TraverseOptions = DefaultDoorMarchTraversalOptions,
): Ray2 | null {
  const { intervals: traverseLoop, collinearJoinedVertices } = loop;
  const pl = polyLine2New(collinearJoinedVertices);
  const traverseLoopLength = traverseLoop.length;

  let distTraveled = 0;
  let intervalCount = -1;

  const wrapBackToFirstEdge = intervalIndex === 0 ? 0 : 1;
  for (let index = 0; index < traverseLoopLength + wrapBackToFirstEdge; index++) {
    const i = (edgeIndex + index) % traverseLoopLength;
    const intervalLength = traverseLoop[i].length;
    for (
      let j = index === 0 ? intervalIndex : 0;
      j < (index === traverseLoopLength ? intervalIndex : intervalLength);
      j++
    ) {
      intervalCount++;

      const interval = traverseLoop[i][j];

      const { action, intervalWidth } = tryPlaceAtWallInterval({
        interval,
        intervalCount,
        distTraveled,
        options,
      });

      switch (action) {
        case IntervalAction.EXIT: {
          return null;
        }
        case IntervalAction.SKIP: {
          // skips the first interval without recording the distance traveled
          if (options.ignoreEntityInterval && intervalCount === 0) {
            continue;
          }
          distTraveled += ensureDefined(intervalWidth);
          continue;
        }
        case IntervalAction.PLACE: {
          const param = interval.right.param + ensureDefined(options.placementIntervalWidth);
          const [x, y] = polyLine2EdgeEvalOffset(pl, i, 0, param);
          const [nx, ny] = polyLine2EdgeNormal(pl, i, true);
          return { originX: x, originY: y, dirX: nx, dirY: ny };

          //Useful for debugging
          // debugPolyLine({s
          //   pline: polyLine2New([sx, sy, ex, ey]),
          //   mat: DebugColor.Red,
          //   id: `${entityId}${front ? "f" : "b"}${"ccw"}`,
          // });
          // debugPoint({
          //   shape: "diamond",
          //   size: 2,
          //   x: ex,
          //   y: ey,
          //   mat: DebugColor.Red,
          //   id: `${entityId}${front ? "f" : "b"}${"ccw"}${"p"}`,
          // });
        }
      }
    }
  }
  return null;
}

/**
 * Function to traverse a wall interval loop in the clockwise direction starting
 * from a given edge and interval index.
 * @param loop
 * @param edgeIndex The index of the edge to start traversing from
 * @param intervalIndex The index of the interval to start traversing from
 * @param options configuration options for the traversal
 * @see traverseWallIntervalLoopCCW
 */
export function traverseWallIntervalLoopCW(
  loop: WallIntervalLoop,
  edgeIndex: number,
  intervalIndex: number,
  options: TraverseOptions = DefaultDoorMarchTraversalOptions,
): Ray2 | null {
  const { intervals: traverseLoop, collinearJoinedVertices } = loop;
  const pl = polyLine2New(collinearJoinedVertices);
  const traverseLoopLength = traverseLoop.length;

  let distTraveled = 0;
  let intervalCount = -1;

  const wrapBackToFirstEdge = intervalIndex - 1 === traverseLoop[edgeIndex].length ? 0 : 1;
  for (let index = 0; index < traverseLoopLength + wrapBackToFirstEdge; index++) {
    const i = (edgeIndex - index + traverseLoopLength) % traverseLoopLength;
    const intervalLength = traverseLoop[i].length;
    for (
      let j = index === 0 ? intervalIndex : intervalLength - 1;
      j > (index === traverseLoopLength ? intervalIndex : -1);
      j--
    ) {
      intervalCount++;

      const interval = traverseLoop[i][j];

      const { action, intervalWidth } = tryPlaceAtWallInterval({
        interval,
        intervalCount,
        distTraveled,
        options,
      });

      switch (action) {
        case IntervalAction.EXIT: {
          return null;
        }
        case IntervalAction.SKIP: {
          if (options.ignoreEntityInterval && intervalCount === 0) {
            continue;
          }
          distTraveled += ensureDefined(intervalWidth);
          continue;
        }
        case IntervalAction.PLACE: {
          const param = interval.left.param - ensureDefined(options.placementIntervalWidth);
          const [x, y] = polyLine2EdgeEvalOffset(pl, i, 0, param);
          const [nx, ny] = polyLine2EdgeNormal(pl, i, true);
          return { originX: x, originY: y, dirX: nx, dirY: ny };

          //Useful for debugging
          // debugPolyLine({
          //   pline: polyLine2New([sx, sy, ex, ey]),
          //   mat: DebugColor.Red,
          //   id: `${entityId}${front ? "f" : "b"}${"cw"}`,
          // });
          // debugPoint({
          //   shape: "diamond",
          //   size: 2,
          //   x: ex,
          //   y: ey,
          //   mat: DebugColor.Red,
          //   id: `${entityId}${front ? "f" : "b"}${"cw"}${"p"}`,
          // });
          //return;
        }
      }
    }
  }
  return null;
}

// Interface to represent the parameters passed to the traversal function
interface TryPlaceAtWallIntervalOptions {
  readonly interval: WallInterval; // The current wall interval being traversed
  readonly intervalCount: number; // The number of intervals that have been traversed so far
  readonly distTraveled: number; // The total distance traveled so far
  readonly options: TraverseOptions; // Options for the traversal function
}

/**
 * Function to determine what action to take based on the current interval and options
 */
function tryPlaceAtWallInterval({ interval, intervalCount, distTraveled, options }: TryPlaceAtWallIntervalOptions) {
  // If the first interval should be ignored and this is the first interval, skip it
  if (options.ignoreEntityInterval && intervalCount === 0) {
    return { action: IntervalAction.SKIP, intervalWidth: 0 };
  }

  // Calculate the width of the current interval
  const intervalWidth = getIntervalWidth(interval);

  // If the traversal has reached the perimeter travel limit, exit the loop
  if (
    options.perimeterTravelLimit !== undefined &&
    options.placementIntervalWidth !== undefined &&
    distTraveled + options.placementIntervalWidth > options.perimeterTravelLimit
  ) {
    return { action: IntervalAction.EXIT, intervalWidth };
  }

  // If the traversal should stop at the next door and this interval includes a door, exit the loop
  const intervalIncludesDoor = interval.entities[WallIntervalEntityType.DOOR];
  if (options.stopAtNextDoor && intervalCount > 0 && intervalIncludesDoor) {
    return { action: IntervalAction.EXIT, intervalWidth };
  }

  // If the interval includes an entity type to be avoided, then skip it
  if (
    (options.avoidDoors && intervalIncludesDoor) ||
    (options.avoidDoorSwings && interval.entities[WallIntervalEntityType.DOOR_SWING]) ||
    (options.avoidWindows && interval.entities[WallIntervalEntityType.WINDOW]) ||
    (options.avoidWallCuts && interval.entities[WallIntervalEntityType.WALL_CUT])
  ) {
    return { action: IntervalAction.SKIP, intervalWidth };
  }

  // If the interval is too small, then skip it
  if (options.minimumIntervalWidth !== undefined && intervalWidth < options.minimumIntervalWidth) {
    return { action: IntervalAction.SKIP, intervalWidth };
  }

  // Otherwise, place an object in the current interval
  return { action: IntervalAction.PLACE, intervalWidth: null };
}
